import {useMemo} from 'react';

import {Table, TableSkeleton, TBody, Td, Th, Thead, Tr} from '@/components';
import type {BusinessLiveAccessNote} from '@/types';
import * as formatDate from '@/utils/date';

const formatAction = (note: BusinessLiveAccessNote): string => {
  const label = note.action.replace(/_/g, ' ');
  return note.documentType ? `${label}: ${note.documentType}` : label;
};

type BusinessLiveAccessNotesTableProps = {
  isLoading: boolean;
  notes?: BusinessLiveAccessNote[];
  resolveActor?: (note: BusinessLiveAccessNote) => string;
};

const BusinessLiveAccessNotesTable = ({
  isLoading,
  notes = [],
  resolveActor = (note) => note.changedBy,
}: BusinessLiveAccessNotesTableProps) => {
  // Newest first for quick scanning (notes are stored append-only / chronological).
  const ordered = useMemo(() => [...notes].reverse(), [notes]);

  const renderNotes = () => {
    if (isLoading) {
      return <TableSkeleton />;
    }

    if (!ordered.length) {
      return (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-3">
          No activity yet.
        </p>
      );
    }

    return (
      <div className="max-h-[400px] overflow-y-auto overflow-x-hidden">
        <Table>
          <Thead>
            <Tr enableHover={false}>
              <Th className="px-4 py-2 text-left">Date</Th>
              <Th className="px-4 py-2 text-left">Actor</Th>
              <Th className="px-4 py-2 text-left">Action</Th>
              <Th className="px-4 py-2 text-left">Status</Th>
              <Th className="px-4 py-2 text-left">Note</Th>
            </Tr>
          </Thead>
          <TBody>
            {ordered.map((note, idx) => (
              <Tr key={idx} data-cy="live-access-note-row">
                <Td className="px-4 py-2">
                  {note.timestamp
                    ? formatDate.DDMonYYYY(String(note.timestamp), true)
                    : '-'}
                </Td>
                <Td className="px-4 py-2" data-cy="live-access-note-actor">
                  {resolveActor(note)} ({note.actorType})
                </Td>
                <Td className="px-4 py-2" data-cy="live-access-note-action">
                  {formatAction(note)}
                </Td>
                <Td className="px-4 py-2" data-cy="live-access-note-status">
                  {note.status ?? '-'}
                </Td>
                <Td className="px-4 py-2" data-cy="live-access-note-text">
                  {note.note ?? '-'}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      </div>
    );
  };

  return (
    <div className="w-full mt-6">
      <h4 className="font-semibold text-gray-900 dark:text-gray-300 mb-2">
        {'Live Access Audit Logs'.toLocaleUpperCase()}
      </h4>
      <hr className="mb-3 dark:border-dark-borderMid" />

      {renderNotes()}
    </div>
  );
};

export default BusinessLiveAccessNotesTable;
